import React, { ChangeEvent } from 'react'
import _ from 'lodash'
import { Form } from 'semantic-ui-react'
import { Maybe } from '@adc/sdk/types'
import { FilterSchema, filters } from '@adc/blueprint'
import Text from '../../text'
import InputField from '../../../formFields/inputField'
import DatePickerField from '../../../formFields/datePickerField'
import DropdownField from '../../../formFields/dropdownField'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

interface FieldProps {
  type: 'Date' | 'input'
  label: string
  name: string
  placeholder?: string
  elmId: string
  value: string | number | Date | undefined
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur: () => void
  onFocus: () => void
  hideWrapper: boolean
  autoComplete: string
  disabledDays?: {
    before?: Maybe<Date>
    after?: Maybe<Date>
  }
}

/**
 * Combo field to support both Date and input fields
 */
const Field: React.FC<FieldProps> = ({ type, ...props }) => {
  if (type === 'Date') {
    return <DatePickerField {...props} />
  } else {
    return <InputField {...props} />
  }
}

const Range = ({
  compact,
  definition,
  value,
  elmId,
  onChange,
}: FilterSchema.RangeFilterProps) => {
  const { actions, data } = filters.hooks.useRangeFilter({
    definition,
    value,
    elmId,
    onChange,
  })

  return (
    <Form className={cx('row-layout', 'range', { date: data.isDate })}>
      <div
        className={cx('row-item', {
          half: definition.controlValueType !== 'Date' && !data.isSelect,
          fluid: data.isSelect,
        })}
      >
        {data.isDate && (
          <Text color='gray' size='tiny' className={cx('description')}>
            From
          </Text>
        )}
        {data.isSelect ? (
          <DropdownField
            compact={compact}
            name={`${definition.name}-min`}
            placeholder={definition.placeholderMin || 'No Min'}
            label='Min'
            elmId={`${elmId}_${definition.name}_min`}
            options={data.optionLists.min ?? undefined}
            value={data.currentValue?.min}
            onChange={(newValue: number) => {
              actions.handleSelectChange(newValue, 'min')
            }}
            search={false}
            wrapSelection={false}
            fluid
            hideWrapper={!definition.styleIntent?.showRangeLabels}
          />
        ) : (
          <Field
            type={data.isDate ? 'Date' : 'input'}
            label='Min'
            name={definition.name}
            placeholder={data.isDate ? 'MM/DD/YYYY' : definition.placeholderMin}
            elmId={`${elmId}_${definition.name}_min`}
            value={data.minValueFormatted}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              actions.onChangeHandler(e, 'min')
            }}
            onBlur={() => actions.onBlurHandler('min')}
            onFocus={() => actions.onFocusHandler()}
            hideWrapper
            autoComplete='off'
            {...(data.isDate
              ? {
                  disabledDays: {
                    after: data.currentValue?.max as Date,
                  },
                }
              : {})}
          />
        )}
      </div>
      {data.isSelect && (
        <Text
          color='gray'
          size='tiny'
          className={cx({
            'range-separator-with-labels':
              definition.styleIntent?.showRangeLabels,
          })}
        >
          —
        </Text>
      )}
      <div
        className={cx('row-item', {
          fluid: data.isSelect,
        })}
      >
        {data.isDate && (
          <Text color='gray' size='tiny' className={cx('description')}>
            To
          </Text>
        )}
        {data.isSelect ? (
          <DropdownField
            compact={compact}
            name={`${definition.name}-min`}
            placeholder={definition.placeholderMax || 'No Max'}
            label='Max'
            elmId={`${elmId}_${definition.name}_max`}
            options={data.optionLists.max ?? undefined}
            value={data.currentValue?.max}
            onChange={(newValue: number) => {
              actions.handleSelectChange(newValue, 'max')
            }}
            search={false}
            wrapSelection={false}
            fluid
            hideWrapper={!definition.styleIntent?.showRangeLabels}
          />
        ) : (
          <Field
            type={data.isDate ? 'Date' : 'input'}
            label=''
            name={definition.name}
            placeholder={data.isDate ? 'MM/DD/YYYY' : definition.placeholderMax}
            elmId={`${elmId}_${definition.name}_max`}
            value={data.maxValueFormatted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              actions.onChangeHandler(e, 'max')
            }}
            onBlur={() => actions.onBlurHandler('max')}
            onFocus={() => actions.onFocusHandler()}
            hideWrapper
            autoComplete='off'
            {...(data.isDate
              ? {
                  disabledDays: {
                    before: data.currentValue?.min as Date,
                  },
                }
              : {})}
          />
        )}
      </div>
    </Form>
  )
}

export default Range
